import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getClientById, updateClient } from '../utils/apiService';
import { getEmptyUpdate, updateTypes } from '../utils/updateModel';

const AddUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [newUpdate, setNewUpdate] = useState(getEmptyUpdate());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadClient = async () => {
      const data = await getClientById(id);
      if (data) {
        setClient(data);
        
        // Initialiser les mises à jour si elles n'existent pas
        if (!data.updates) {
          data.updates = [];
        }
      }
      setLoading(false);
    };
    
    loadClient();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUpdate({ ...newUpdate, [name]: value });
  };

  const handleSectionChange = (section, field, value) => {
    setNewUpdate({
      ...newUpdate,
      [section]: {
        ...newUpdate[section],
        [field]: value
      }
    });
  };

  const handleNestedSectionChange = (section, subsection, field, value) => {
    setNewUpdate({
      ...newUpdate,
      [section]: {
        ...newUpdate[section],
        [subsection]: {
          ...newUpdate[section][subsection],
          [field]: value
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!client) return;
    
    // Mettre à jour automatiquement les versions des briques logicielles si des données sont fournies
    const updatedServers = [...client.servers];
    
    if (newUpdate.updateType && newUpdate.commits) {
      // Parcourir tous les serveurs
      updatedServers.forEach(server => {
        // Vérifier si des versions sont fournies dans la mise à jour
        if (newUpdate.commits.manager && server.gitManagerFrontAppVersion) {
          server.gitManagerFrontAppVersion = newUpdate.commits.manager;
        }
        
        if (newUpdate.commits.marshallerFrontal && server.gitMarshallerVersion) {
          server.gitMarshallerVersion = newUpdate.commits.marshallerFrontal;
        }
        
        if (newUpdate.commits.marshallerInterop && server.gitMarshallerInteropVersion) {
          server.gitMarshallerInteropVersion = newUpdate.commits.marshallerInterop;
        }
        
        if (newUpdate.commits.patientApp && server.gitPatientAppVersion) {
          server.gitPatientAppVersion = newUpdate.commits.patientApp;
        }
      });
    }
    
    // Ajouter la nouvelle mise à jour aux mises à jour existantes
    const updatedClient = {
      ...client,
      servers: updatedServers,
      updates: [...(client.updates || []), newUpdate]
    };
    
    // Mettre à jour le client
    const result = await updateClient(client.id, updatedClient);
    
    if (result) {
      navigate(`/client/${client.id}`);
    } else {
      alert('Erreur lors de l\'ajout de la mise à jour');
    }
  };

  if (loading) {
    return <div className="text-center py-10">Chargement...</div>;
  }

  if (!client) {
    return (
      <div className="text-center py-10">
        <h2 className="text-2xl font-bold mb-4">Client non trouvé</h2>
        <button
          onClick={() => navigate('/')}
          className="text-blue-600 hover:underline"
        >
          Retour à la liste des clients
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Ajouter une mise à jour pour {client.name}</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
        <div className="p-6">
          {/* Section d'informations générales sur la mise à jour */}
          <div className="mb-6 border-b pb-6">
            <h2 className="text-xl font-semibold mb-4">Informations de la mise à jour</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="title">
                  Titre *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={newUpdate.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="date">
                  Date
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={new Date(newUpdate.date).toISOString().split('T')[0]}
                  onChange={(e) => setNewUpdate({ ...newUpdate, date: new Date(e.target.value).toISOString() })}
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Type de mise à jour *</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {updateTypes.map(type => (
                  <div key={type} className="flex items-center">
                    <input
                      type="radio"
                      id={`updateType${type}`}
                      name="updateType"
                      value={type}
                      className="w-5 h-5 mr-2"
                      checked={newUpdate.updateType === type}
                      onChange={() => setNewUpdate({ ...newUpdate, updateType: type })}
                      required
                    />
                    <label htmlFor={`updateType${type}`} className={`font-medium ${type === 'Production' ? 'text-purple-600' : 'text-amber-600'}`}>
                      {type}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="ticketUrl">
                Lien du ticket Jira/Taiga
              </label>
              <input
                type="url"
                id="ticketUrl"
                name="ticketUrl"
                className="w-full p-3 border border-gray-300 rounded"
                value={newUpdate.ticketUrl}
                onChange={handleInputChange}
                placeholder="https://hoppen-care.atlassian.net/browse/ENGSUP-12345"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="description">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows="3"
                className="w-full p-3 border border-gray-300 rounded"
                value={newUpdate.description}
                onChange={handleInputChange}
              ></textarea>
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Statut de l'intervention</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="statusCompleted"
                    name="status"
                    value="completed"
                    className="w-5 h-5 mr-2"
                    checked={newUpdate.status === 'completed'}
                    onChange={() => setNewUpdate({ ...newUpdate, status: 'completed' })}
                  />
                  <label htmlFor="statusCompleted" className="text-green-600 font-medium">
                    Complétée avec succès
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="statusPartial"
                    name="status"
                    value="partial"
                    className="w-5 h-5 mr-2"
                    checked={newUpdate.status === 'partial'}
                    onChange={() => setNewUpdate({ ...newUpdate, status: 'partial' })}
                  />
                  <label htmlFor="statusPartial" className="text-yellow-600 font-medium">
                    Partiellement réalisée
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="statusCancelled"
                    name="status"
                    value="cancelled"
                    className="w-5 h-5 mr-2"
                    checked={newUpdate.status === 'cancelled'}
                    onChange={() => setNewUpdate({ ...newUpdate, status: 'cancelled' })}
                  />
                  <label htmlFor="statusCancelled" className="text-red-600 font-medium">
                    Annulée
                  </label>
                </div>
              </div>
              
              {(newUpdate.status === 'cancelled' || newUpdate.status === 'partial') && (
                <div className="mt-3">
                  <label className="block text-gray-700 mb-2" htmlFor="statusReason">
                    Raison {newUpdate.status === 'cancelled' ? "de l'annulation" : "de la réalisation partielle"} *
                  </label>
                  <textarea
                    id="statusReason"
                    name="statusReason"
                    rows="2"
                    className="w-full p-3 border border-gray-300 rounded"
                    value={newUpdate.statusReason}
                    onChange={handleInputChange}
                    required={newUpdate.status === 'cancelled' || newUpdate.status === 'partial'}
                    placeholder={newUpdate.status === 'cancelled' 
                      ? "Expliquez pourquoi l'intervention a été annulée" 
                      : "Expliquez quelles parties ont été réalisées et lesquelles n'ont pas pu l'être"}
                  ></textarea>
                </div>
              )}
            </div>
          </div>

          {/* Section MISE À JOUR */}
          <div className="mb-6 border-b pb-6">
            <h2 className="text-xl font-semibold mb-4">MISE À JOUR</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 mb-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="dbSchemaUpdated"
                  className="w-5 h-5 mr-2"
                  checked={newUpdate.update.dbSchemaUpdated}
                  onChange={(e) => handleSectionChange('update', 'dbSchemaUpdated', e.target.checked)}
                  disabled={newUpdate.status === 'cancelled'}
                />
                <label htmlFor="dbSchemaUpdated" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                  MAJ du schéma de base de données
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="servicesRestarted"
                  className="w-5 h-5 mr-2"
                  checked={newUpdate.update.servicesRestarted}
                  onChange={(e) => handleSectionChange('update', 'servicesRestarted', e.target.checked)}
                  disabled={newUpdate.status === 'cancelled'}
                />
                <label htmlFor="servicesRestarted" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                  Redémarrage des services
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="mobileAppUpdated"
                  className="w-5 h-5 mr-2"
                  checked={newUpdate.update.mobileAppUpdated}
                  onChange={(e) => handleSectionChange('update', 'mobileAppUpdated', e.target.checked)}
                  disabled={newUpdate.status === 'cancelled'}
                />
                <label htmlFor="mobileAppUpdated" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                  MAJ Application mobile
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="buildsUpdated"
                  className="w-5 h-5 mr-2"
                  checked={newUpdate.update.buildsUpdated}
                  onChange={(e) => handleSectionChange('update', 'buildsUpdated', e.target.checked)}
                  disabled={newUpdate.status === 'cancelled'}
                />
                <label htmlFor="buildsUpdated" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                  Mise à jour des builds
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="mobileAppCreated"
                  className="w-5 h-5 mr-2"
                  checked={newUpdate.update.mobileAppCreated}
                  onChange={(e) => handleSectionChange('update', 'mobileAppCreated', e.target.checked)}
                  disabled={newUpdate.status === 'cancelled'}
                />
                <label htmlFor="mobileAppCreated" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                  Création application mobile
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="internalTestsCompleted"
                  className="w-5 h-5 mr-2"
                  checked={newUpdate.update.internalTestsCompleted}
                  onChange={(e) => handleSectionChange('update', 'internalTestsCompleted', e.target.checked)}
                  disabled={newUpdate.status === 'cancelled'}
                />
                <label htmlFor="internalTestsCompleted" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                  Tests internes de l'application mobile réalisés
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="appDeployedToStores"
                  className="w-5 h-5 mr-2"
                  checked={newUpdate.update.appDeployedToStores}
                  onChange={(e) => handleSectionChange('update', 'appDeployedToStores', e.target.checked)}
                  disabled={newUpdate.status === 'cancelled'}
                />
                <label htmlFor="appDeployedToStores" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                  Mise en téléchargement de l'application sur les stores
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="updateNotes">
                Notes supplémentaires
              </label>
              <textarea
                id="updateNotes"
                className="w-full p-3 border border-gray-300 rounded"
                rows="2"
                value={newUpdate.update.notes}
                onChange={(e) => handleSectionChange('update', 'notes', e.target.value)}
              ></textarea>
            </div>
          </div>

          {/* Section GENERAL */}
          <div className="mb-6 border-b pb-6">
            <h2 className="text-xl font-semibold mb-4">GENERAL</h2>
            
            <div className="mb-4">
              <h3 className="font-medium mb-2">Relevé de production</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 pl-4 mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="secureClientConnection"
                    className="w-5 h-5 mr-2"
                    checked={newUpdate.general.production.secureClientConnection}
                    onChange={(e) => handleNestedSectionChange('general', 'production', 'secureClientConnection', e.target.checked)}
                    disabled={newUpdate.status === 'cancelled'}
                  />
                  <label htmlFor="secureClientConnection" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                    Connexion sécurisée chez le client
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="softwareComponentsRecovery"
                    className="w-5 h-5 mr-2"
                    checked={newUpdate.general.production.softwareComponentsRecovery}
                    onChange={(e) => handleNestedSectionChange('general', 'production', 'softwareComponentsRecovery', e.target.checked)}
                    disabled={newUpdate.status === 'cancelled'}
                  />
                  <label htmlFor="softwareComponentsRecovery" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                    Récupération des briques logicielles avec Git
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="softwareDependenciesRecovery"
                    className="w-5 h-5 mr-2"
                    checked={newUpdate.general.production.softwareDependenciesRecovery}
                    onChange={(e) => handleNestedSectionChange('general', 'production', 'softwareDependenciesRecovery', e.target.checked)}
                    disabled={newUpdate.status === 'cancelled'}
                  />
                  <label htmlFor="softwareDependenciesRecovery" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                    Récupération des dépendances du logiciel
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <h3 className="font-medium mb-2">Vérification</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 pl-4 mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="carePortalDisplay"
                    className="w-5 h-5 mr-2"
                    checked={newUpdate.general.verification.carePortalDisplay}
                    onChange={(e) => handleNestedSectionChange('general', 'verification', 'carePortalDisplay', e.target.checked)}
                    disabled={newUpdate.status === 'cancelled'}
                  />
                  <label htmlFor="carePortalDisplay" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                    Afficher le portail soignant
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="carePortalLogin"
                    className="w-5 h-5 mr-2"
                    checked={newUpdate.general.verification.carePortalLogin}
                    onChange={(e) => handleNestedSectionChange('general', 'verification', 'carePortalLogin', e.target.checked)}
                    disabled={newUpdate.status === 'cancelled'}
                  />
                  <label htmlFor="carePortalLogin" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                    Se connecter au portail soignant avec un identifiant soignant
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="patientSearch"
                    className="w-5 h-5 mr-2"
                    checked={newUpdate.general.verification.patientSearch}
                    onChange={(e) => handleNestedSectionChange('general', 'verification', 'patientSearch', e.target.checked)}
                    disabled={newUpdate.status === 'cancelled'}
                  />
                  <label htmlFor="patientSearch" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                    Effectuer une recherche patient
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="pairingSheetGeneration"
                    className="w-5 h-5 mr-2"
                    checked={newUpdate.general.verification.pairingSheetGeneration}
                    onChange={(e) => handleNestedSectionChange('general', 'verification', 'pairingSheetGeneration', e.target.checked)}
                    disabled={newUpdate.status === 'cancelled'}
                  />
                  <label htmlFor="pairingSheetGeneration" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                    Générer une feuille d'appairage
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="patientPortalDisplay"
                    className="w-5 h-5 mr-2"
                    checked={newUpdate.general.verification.patientPortalDisplay}
                    onChange={(e) => handleNestedSectionChange('general', 'verification', 'patientPortalDisplay', e.target.checked)}
                    disabled={newUpdate.status === 'cancelled'}
                  />
                  <label htmlFor="patientPortalDisplay" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                    Afficher le portail patient
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="patientPortalLogin"
                    className="w-5 h-5 mr-2"
                    checked={newUpdate.general.verification.patientPortalLogin}
                    onChange={(e) => handleNestedSectionChange('general', 'verification', 'patientPortalLogin', e.target.checked)}
                    disabled={newUpdate.status === 'cancelled'}
                  />
                  <label htmlFor="patientPortalLogin" className={newUpdate.status === 'cancelled' ? 'text-gray-400' : ''}>
                    Se connecter au portail patient
                  </label>
                </div>
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="verificationNotes">
                  Notes de vérification
                </label>
                <textarea
                  id="verificationNotes"
                  className="w-full p-3 border border-gray-300 rounded"
                  rows="2"
                  value={newUpdate.general.verification.notes}
                  onChange={(e) => handleNestedSectionChange('general', 'verification', 'notes', e.target.value)}
                ></textarea>
              </div>
            </div>
          </div>
          
          {/* Section COMMITS */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4">COMMITS</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="manager">
                  MANAGER
                </label>
                <input
                  type="text"
                  id="manager"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={newUpdate.commits.manager}
                  onChange={(e) => handleSectionChange('commits', 'manager', e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="marshallerFrontal">
                  MARSHALLER-FRONTAL
                </label>
                <input
                  type="text"
                  id="marshallerFrontal"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={newUpdate.commits.marshallerFrontal}
                  onChange={(e) => handleSectionChange('commits', 'marshallerFrontal', e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="marshallerInterop">
                  MARSHALLER-INTEROP
                </label>
                <input
                  type="text"
                  id="marshallerInterop"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={newUpdate.commits.marshallerInterop}
                  onChange={(e) => handleSectionChange('commits', 'marshallerInterop', e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="patientApp">
                  PATIENT-APP
                </label>
                <input
                  type="text"
                  id="patientApp"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={newUpdate.commits.patientApp}
                  onChange={(e) => handleSectionChange('commits', 'patientApp', e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="datamodel">
                  DATAMODEL
                </label>
                <input
                  type="text"
                  id="datamodel"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={newUpdate.commits.datamodel}
                  onChange={(e) => handleSectionChange('commits', 'datamodel', e.target.value)}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="commitsNotes">
                Notes supplémentaires sur les commits
              </label>
              <textarea
                id="commitsNotes"
                className="w-full p-3 border border-gray-300 rounded"
                rows="2"
                value={newUpdate.commits.notes}
                onChange={(e) => handleSectionChange('commits', 'notes', e.target.value)}
              ></textarea>
            </div>
          </div>
          
          {/* Notes additionnelles */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2" htmlFor="additionalNotes">
              Notes additionnelles
            </label>
            <textarea
              id="additionalNotes"
              name="additionalNotes"
              rows="4"
              className="w-full p-3 border border-gray-300 rounded"
              value={newUpdate.additionalNotes}
              onChange={handleInputChange}
            ></textarea>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/client/${client.id}`)}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Enregistrer la mise à jour
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddUpdate;
